import React from 'react';

import { renderToStaticMarkup } from 'react-dom/server';

import eyeIcon from 'assets/images/eye.svg';
import eyeIconCross from 'assets/images/eyeCross.svg';
import penIcon from 'assets/images/pen.svg';
import penIconCross from 'assets/images/penCross.svg';
import qrcode from 'assets/images/qrcode.svg';
import saveIconDisabled from 'assets/images/saveDisabled.svg';
import { findFilledIcon } from 'components/icon-picker';
import PlanPinSvg from 'components/PlanViewer/PlanPinSvg';
import { getFileTypeFromBlob } from 'utils/fileType';
import { generateQRCode } from 'utils/qrcode/qrcode';
import { replaceUrlFallback } from 'utils/url.utils';

const QR_CODE_SIZE = 800;
const PDFTRON_FCO_TYPE = 'x-fcoType';
const PDFTRON_FCO_TYPE_QRCODE = 'QR_CODE';

const MIME_EXTENSION_MAP = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
};

export const displayFirstPageOnly = docViewer => {
  const pageCount = docViewer.getPageCount();
  const pagesToRemoveArray = Array.from({ length: pageCount - 1 }, (_, i) => i + 2); // pageCount = 4 => [2, 3, 4]
  const doc = docViewer.getDocument();
  doc.removePages(pagesToRemoveArray);
};

let throttleTime = null;

export const onViewerScroll = (e, documentViewer) => {
  e.preventDefault();
  e.stopPropagation();

  if (e.ctrlKey || e.metaKey) {
    if (!throttleTime) {
      throttleTime = setTimeout(() => {
        const scaleFactor = e.deltaMode === 0 ? 0.05 : 0.4;
        const newZoom =
          documentViewer.getZoomLevel() * (e.deltaY < 0 ? 1 + scaleFactor : 1 - scaleFactor);

        if (newZoom <= 80 && newZoom >= 0.2) {
          documentViewer.zoomToMouse(newZoom, 0, 76);
        }
        throttleTime = null;
      }, 50);
    }
    return;
  }

  const scrollView = documentViewer.getScrollViewElement();
  if (scrollView) {
    const multiplier = e.deltaMode === 1 ? 40 : 1;
    scrollView.scrollLeft += e.deltaX * multiplier;
    scrollView.scrollTop += e.deltaY * multiplier;
  }
};

const createPinIdNode = pin => {
  const textNode = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  textNode.setAttribute('text-anchor', 'middle');
  textNode.setAttribute('font-size', '12');
  textNode.setAttribute('font-weight', 'bold');
  textNode.setAttribute('fill', '#000');
  textNode.setAttribute('transform', 'translate(30 75)');
  textNode.setAttribute(
    'style',
    'text-shadow: 2px 0 0 #fff, -2px 0 0 #fff, 0 2px 0 #fff, 0 -2px 0 #fff, 1px 1px #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff',
  );
  textNode.innerHTML = `#${pin?.legacy_id}`;
  return textNode;
};

export const createPinStampImageData = ({ pin, modules, withCrossHair }) => {
  const module = modules.find(m => m.id === pin.module_id);

  const IconComponent = findFilledIcon({ name: module?.icon });
  const parser = new DOMParser();
  const moduleIcon = renderToStaticMarkup(<IconComponent />);

  const pinSvg = renderToStaticMarkup(
    <PlanPinSvg
      color={pin.color || module?.color}
      key={pin.id}
      height={withCrossHair ? 85 : 75}
      showCrossHair={withCrossHair}
    />,
  );

  const pinSvgDOM = parser.parseFromString(pinSvg, 'image/svg+xml');
  const moduleIconSVG = parser.parseFromString(moduleIcon, 'image/svg+xml');

  const { children } = moduleIconSVG.getElementsByTagName('svg')[0];
  for (let i = 0; i < children.length; i++) {
    const newChild = children[i].cloneNode(true);
    newChild.setAttribute('transform', 'translate(18, 18)');
    newChild.setAttribute('fill', '#fff');
    pinSvgDOM.getElementsByTagName('svg')[0].appendChild(newChild.cloneNode(true));
  }
  if (pin.legacy_id) {
    pinSvgDOM.getElementsByTagName('svg')[0].appendChild(createPinIdNode(pin));
  }
  return pinSvgDOM.getElementsByTagName('svg')[0];
};

// Detects the right "extension" hint to pass to PDFTron based on the actual
// binary content of the blob, falling back to "pdf" when unknown.
export const detectExtensionFromBlob = async blob => {
  try {
    const detected = await getFileTypeFromBlob(blob);
    return MIME_EXTENSION_MAP[detected] || 'pdf';
  } catch {
    return null;
  }
};

// Fetches the document at the given URL and loads it into a PDFTron viewer.
// `loadDocument` is the actual loader to use (documentViewer.loadDocument for
// the lite viewer, instance.UI.loadDocument for the full one). Returns the
// fetched blob so callers may store it or compute hashes if needed.
export const loadDocumentBlobFromUrl = async ({ url, loadDocument, extraOptions = {} }) => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch PDF: ${res.status}`);
  }

  const blob = await res.blob();
  const extension = await detectExtensionFromBlob(blob);
  const options = extension ? { ...extraOptions, extension } : extraOptions;
  await loadDocument(blob, options);
  return blob;
};

// Translates a click event on the PDFTron viewer into normalised pin
// coordinates, taking the document rotation into account.
export const computePinPositionFromClick = (documentViewer, event) => {
  const pagePoint = documentViewer.getViewerCoordinatesFromMouseEvent(event);
  if (!pagePoint) return null;

  const pageWidth = documentViewer.getPageWidth(1);
  const pageHeight = documentViewer.getPageHeight(1);
  const x = pagePoint.x < 0 ? 0 : pagePoint.x;
  const y = pagePoint.y < 0 ? 0 : pagePoint.y;
  const rotation = documentViewer.getCompleteRotation(1);

  switch (rotation) {
    case 1:
      return { x: 1 - y / pageHeight, y: x / pageWidth };
    case 2:
      return { x: 1 - x / pageWidth, y: 1 - y / pageHeight };
    case 3:
      return { x: y / pageHeight, y: 1 - x / pageWidth };
    default:
      return { x: x / pageWidth, y: y / pageHeight };
  }
};

export const buildUpdatePlanPayload = (plan, blob, spark2, uuidBlobFile) => ({
  ...plan,
  resources: {
    ...plan?.media_resource,
    newBlob: blob,
    hash: spark2 && spark2.end(),
    type: blob.type,
    name: plan?.name,
    size: blob.size,
    uuidBlobFile,
  },
  size: blob.size,
});

export const buildUpdateDocumentPayload = (pdfDocument, blob, spark2, uuidBlobFile) => ({
  ...pdfDocument,
  media_resource: {
    ...pdfDocument?.media_resource,
    newBlob: blob,
    hash: spark2 && spark2.end(),
    type: blob.type,
    name: pdfDocument?.name,
    size: blob.size,
    uuidBlobFile,
  },
  size: blob.size,
  parent_id: !pdfDocument?.parent_id ? 'root' : pdfDocument.parent_id,
});

// Stamps the supplied image (typically a QR code) as an annotation on the
// first page of the document, sized to fit a quarter of the page.
export const addImageAsAnnotation = (annotationManager, Annotations, pdfDocument, imageSrc) => {
  const pageInfo = pdfDocument.getPageInfo(1);
  const maxSize = Math.min(QR_CODE_SIZE, pageInfo.width / 4, pageInfo.height / 4);

  const stampAnnotation = new Annotations.StampAnnotation();
  stampAnnotation.PageNumber = 1;
  stampAnnotation.X = pageInfo.width - maxSize - 16;
  stampAnnotation.Y = pageInfo.height - maxSize - 16;
  stampAnnotation.Width = maxSize;
  stampAnnotation.Height = maxSize;
  stampAnnotation.setImageData(imageSrc);
  stampAnnotation.Author = annotationManager.getCurrentUser();
  stampAnnotation.setCustomData(PDFTRON_FCO_TYPE, PDFTRON_FCO_TYPE_QRCODE);

  annotationManager.addAnnotation(stampAnnotation);
  annotationManager.redrawAnnotation(stampAnnotation);
};

export const createQrCodeButton = ({ instance, title }) => {
  const { annotationManager, documentViewer, Annotations } = instance.Core;
  return new instance.UI.Components.CustomButton({
    dataElement: 'qrCodeButton',
    img: qrcode,
    title,
    onClick: () => {
      generateQRCode(replaceUrlFallback(window.location.href), QR_CODE_SIZE).then(data => {
        addImageAsAnnotation(
          annotationManager,
          Annotations,
          documentViewer.getDocument(),
          `data:image/png;base64,${data}`,
        );
      });
    },
  });
};

// Creates a stateful "eye"-style toggle button, reused for both items and
// annotations visibility.
const createToggleButton = ({
  instance,
  dataElement,
  initiallyVisible,
  iconVisible,
  iconHidden,
  titleHide,
  titleShow,
  onToggle,
}) =>
  new instance.UI.Components.StatefulButton({
    style: { backgroundColor: 'transparent' },
    dataElement,
    initialState: initiallyVisible ? 'Visible' : 'Hidden',
    states: {
      Visible: {
        img: iconVisible,
        title: titleHide,
        onClick: update => {
          onToggle();
          update('Hidden');
        },
      },
      Hidden: {
        img: iconHidden,
        title: titleShow,
        onClick: update => {
          onToggle();
          update('Visible');
        },
      },
    },
  });

export const createItemVisibilityButton = ({
  instance,
  intl,
  messages,
  initiallyVisible,
  onToggle,
}) =>
  createToggleButton({
    instance,
    dataElement: 'itemVisibilityButton',
    initiallyVisible,
    iconVisible: eyeIcon,
    iconHidden: eyeIconCross,
    titleHide: intl.formatMessage(messages.toolitp_hide_items),
    titleShow: intl.formatMessage(messages.toolitp_show_items),
    onToggle,
  });

export const createAnnotationsVisibilityButton = ({
  instance,
  intl,
  messages,
  initiallyVisible,
  onToggle,
}) =>
  createToggleButton({
    instance,
    dataElement: 'annotationsVisibilityButton',
    initiallyVisible,
    iconVisible: penIcon,
    iconHidden: penIconCross,
    titleHide: intl.formatMessage(messages.toolitp_hide_annotaitons),
    titleShow: intl.formatMessage(messages.toolitp_show_annotaitons),
    onToggle,
  });

export const createSaveButton = ({ instance, title, onClick }) =>
  new instance.UI.Components.CustomButton({
    dataElement: 'saveButton',
    img: saveIconDisabled,
    title,
    onClick,
  });
